import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mts/ui';
import { fetchLatestListingReview } from '../../../services/api';

function formatReviewDate(value) {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function renderStars(rating) {
  const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(5, Math.round(rating))) : 0;
  return '★★★★★'.slice(0, safeRating) + '☆☆☆☆☆'.slice(0, 5 - safeRating);
}

function BasicReviewsCard({ listingSlug, categorySlug = null }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!listingSlug) {
      setReview(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    fetchLatestListingReview(listingSlug, { categorySlug })
      .then((data) => {
        if (!cancelled) {
          setReview(data || null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReview(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [listingSlug, categorySlug]);

  const reviewDateLabel = useMemo(() => {
    if (!review) return '';
    return formatReviewDate(review.publishTime) || review.relativePublishTimeDescription || '';
  }, [review]);

  return (
    <div id="ReviewsCard">
      <Card
        id="ReviewsCard-Card"
        className="my-[10px] rounded-lg"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
      <Card.Header id="ReviewsCard-CardHeader" className="flex items-start justify-between gap-3">
        <div id="ReviewsCard-CardHeaderInner">
          <h2 className="text-base font-semibold text-[var(--primary)]">Reviews</h2>
          <Card.Title id="ReviewsCard-CardTitle">
            <span className="sr-only">Customer reviews</span>
          </Card.Title>
          <Card.Description id="ReviewsCard-CardDescription">
            <span className="sr-only">Latest Google review</span>
          </Card.Description>
        </div>
        <Card.Action id="ReviewsCard-CardAction">
          <div />
        </Card.Action>
      </Card.Header>

      <Card.Content id="ReviewsCard-CardContent" className="before:content-none after:content-none">
        {loading ? <p className="text-sm text-black/70">Loading latest review...</p> : null}

        {!loading && review ? (
          <div className="rounded-lg border border-[var(--ui-input-border)] bg-[var(--card)] p-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-black">{review.authorName || 'Google user'}</p>
              <p className="text-xs text-black/60">{reviewDateLabel}</p>
            </div>

            {review.rating !== null && review.rating !== undefined ? (
              <p className="mb-2 text-sm text-black/80">{renderStars(review.rating)} · {review.rating.toFixed(1)}</p>
            ) : null}

            <p className="text-sm leading-6 text-black/80">{review.text || 'No review text available.'}</p>
          </div>
        ) : null}

        {!loading && !review ? <p className="text-sm text-black/70">No recent Google reviews available.</p> : null}
      </Card.Content>

      <Card.Footer id="ReviewsCard-CardFooter">
        <div />
      </Card.Footer>
      </Card>
    </div>
  );
}

export default BasicReviewsCard;
